import { useState, useEffect, useCallback } from 'react';
import { getTexture, loadTexture, preloadTextures, getCacheStats } from '@/utils/textureCache';

export function useTexture(url) {
  const [texture, setTexture] = useState(() => getTexture(url));
  const [loading, setLoading] = useState(!texture && !!url);

  useEffect(() => {
    if (!url) {
      setTexture(null);
      setLoading(false);
      return;
    }

    const cached = getTexture(url);
    if (cached) {
      setTexture(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    loadTexture(url).then((loadedTexture) => {
      setTexture(loadedTexture);
      setLoading(false);
    });
  }, [url]);

  return { texture, loading };
}

export function usePreloadTextures(urls, options = {}) {
  const [progress, setProgress] = useState(0);
  const [isPreloading, setIsPreloading] = useState(false);

  const preload = useCallback(() => {
    if (isPreloading || !urls?.length) return Promise.resolve();

    setIsPreloading(true);
    setProgress(0);

    return preloadTextures(urls, {
      ...options,
      onProgress: (p) => {
        setProgress(p);
        options.onProgress?.(p);
      },
    }).finally(() => {
      setIsPreloading(false);
    });
  }, [urls, isPreloading, options]);

  return { preload, progress, isPreloading };
}

export function useTextureCacheStats() {
  const [stats, setStats] = useState(getCacheStats);

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(getCacheStats());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return stats;
}
